import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import {
  getVideoCredits,
  getCreditPacks,
  buyCreditPack,
  confirmCreditPurchase,
  VideoCredits as Credits,
  CreditPack,
} from "../services/mediaService";

const VideoCredits = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [credits, setCredits] = useState<Credits | null>(null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        // Coming back from Paystack: confirm the payment first, so the new credits show
        const reference =
          searchParams.get("reference") || searchParams.get("trxref");
        if (reference) {
          try {
            const newBalance = await confirmCreditPurchase(reference);
            setMessage(
              `Payment successful! You now have ${newBalance} video credits.`,
            );
          } catch (err: any) {
            setError(
              err.response?.data?.message ||
                "We couldn't confirm your payment. If you were charged, please contact us.",
            );
          }
        }
        const [c, p] = await Promise.all([getVideoCredits(), getCreditPacks()]);
        setCredits(c);
        setPacks(p);
      } catch {
        setError("Could not load your credits. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBuy = async (pack: string) => {
    setError("");
    setBuying(pack);
    try {
      const url = await buyCreditPack(pack);
      window.location.href = url; // go to Paystack
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          "Could not start the payment. Please try again.",
      );
      setBuying(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Video credits</h1>
        <p className="text-gray-400 text-sm md:text-base mb-8">
          1 credit = 1 video shot (about 8 seconds). Credits never expire.
        </p>

        {message && (
          <div className="bg-green-900 border border-green-700 text-green-300 px-4 py-3 rounded-lg text-sm mb-6">
            {message}
          </div>
        )}
        {error && (
          <div className="bg-red-900 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : credits && !credits.canUseVideo ? (
          /* Free plan users need PRO or Agency first */
          <div className="bg-gray-800 border border-yellow-600 rounded-xl p-6">
            <p className="text-yellow-400 mb-4">
              Video shots are available on PRO and Agency plans.
            </p>
            <button
              onClick={() => navigate("/pricing")}
              className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-xl font-semibold transition"
            >
              See plans
            </button>
          </div>
        ) : (
          <>
            {/* Current balance */}
            {credits && (
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-8 flex flex-wrap gap-8">
                <div>
                  <p className="text-gray-400 text-xs uppercase">Credits</p>
                  <p className="text-3xl font-bold text-purple-400">
                    {credits.credits}
                  </p>
                </div>
                {credits.freeClipsPerMonth > 0 && (
                  <div>
                    <p className="text-gray-400 text-xs uppercase">
                      Free clips this month
                    </p>
                    <p className="text-3xl font-bold text-green-400">
                      {credits.freeClipsLeft}
                      <span className="text-gray-500 text-base">
                        {" "}
                        / {credits.freeClipsPerMonth}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Credit packs */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {packs.map((p) => (
                <div
                  key={p.pack}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-6 flex flex-col"
                >
                  <p className="text-3xl font-bold">{p.credits}</p>
                  <p className="text-gray-400 text-sm mb-4">video credits</p>
                  <p className="text-xl font-semibold mb-1">
                    ₦{p.priceNaira.toLocaleString()}
                  </p>
                  <p className="text-gray-500 text-xs mb-6">
                    ₦{Math.round(p.priceNaira / p.credits).toLocaleString()} per
                    clip
                  </p>
                  <button
                    onClick={() => handleBuy(p.pack)}
                    disabled={buying !== null}
                    className="mt-auto bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-3 rounded-xl font-semibold transition"
                  >
                    {buying === p.pack ? "Opening Paystack..." : "Buy"}
                  </button>
                </div>
              ))}
            </div>

            <p className="text-gray-500 text-xs mt-6">
              If a video fails, your credit is returned automatically.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoCredits;
